import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'

import { InviteAcceptPage } from '@nebula/shared/components'
import { OrgChoicePage } from '@nebula/shared/components/OrgChoicePage'
import { useAuthStore, usePortalStore } from '@nebula/shared/store'

import MainLayout from './layouts/MainLayout'
import AccountPage from './pages/AccountPage'
import AnalyticsPage from './pages/AnalyticsPage'
import BackupsPage from './pages/BackupsPage'
import BillingPage from './pages/BillingPage'
import CaptiveCustomerNoticePage from './pages/CaptiveCustomerNoticePage'
import DashboardPage from './pages/DashboardPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import GatewayPage from './pages/GatewayPage'
import InstanceDetailPage from './pages/InstanceDetailPage'
import InstancesPage from './pages/InstancesPage'
import LoginPage from './pages/LoginPage'
import MarketplacePage from './pages/MarketplacePage'
import MonitoringPage from './pages/MonitoringPage'
import MyAssociationsPage from './pages/MyAssociationsPage'
import NetworkingPage from './pages/NetworkingPage'
import NotificationsPage from './pages/NotificationsPage'
import RegisterPage from './pages/RegisterPage'
import ResellersPage from './pages/ResellersPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import StoragePage from './pages/StoragePage'
import SupportPage from './pages/SupportPage'
import TeamPage from './pages/TeamPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import VMConsolePopupPage from './pages/VMConsolePopupPage'

type PortalFeatureKey =
  | 'userRegistration'
  | 'passwordReset'
  | 'dashboard'
  | 'marketplace'
  | 'instances'
  | 'monitoring'
  | 'backups'
  | 'networking'
  | 'gateway'
  | 'storage'
  | 'analytics'
  | 'team'
  | 'billing'
  | 'support'
  | 'notifications'

// Componente para proteger rotas de clientes cativos
function FreeCustomerOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  const isCaptiveCustomer = user?.organization?.parent_id != null

  if (isCaptiveCustomer) {
    return <CaptiveCustomerNoticePage />
  }

  return <>{children}</>
}

function PortalFeatureRoute({
  feature,
  children,
}: {
  feature: PortalFeatureKey
  children: React.ReactNode
}) {
  const { theme, isPortalDomain, isLoading } = usePortalStore()

  if (isPortalDomain && !isLoading && theme && theme.featuresEnabled[feature] === false) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function App() {
  const { user, checkAuth, isLoading, orgChoiceRequired } = useAuthStore()
  const { fetchTheme, theme, isPortalDomain, isLoading: isPortalLoading } = usePortalStore()
  const location = useLocation()

  useEffect(() => {
    checkAuth()
    fetchTheme()
  }, [checkAuth, fetchTheme])

  // Redirect após login se veio de convite
  useEffect(() => {
    if (user) {
      const pendingRedirect = sessionStorage.getItem('invite_redirect')
      if (pendingRedirect) {
        sessionStorage.removeItem('invite_redirect')
        window.location.replace(pendingRedirect)
      }
    }
  }, [user])

  // Check if on public auth pages to avoid remounting while checking auth.
  const isAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/register' ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password' ||
    location.pathname === '/verify-email' ||
    location.pathname === '/'

  // Debug logs detalhados

  // Primeira visita em domínio de portal (sem cache): aguardar tema carregar
  // Fundo neutro escuro para evitar flash do tema padrão roxo
  if (isPortalLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ backgroundColor: '#030712' }}
      >
        <div className="size-10 animate-spin rounded-full border-2 border-gray-700 border-t-gray-400"></div>
      </div>
    )
  }

  // Mostrar loading enquanto verifica autenticação (mas NÃO na página de login/register)
  if (isLoading && !isAuthPage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-b-2 border-purple-600"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  // Se não tem usuário, mostrar rotas públicas (login/register/verify-email)
  if (!user) {
    const registrationEnabled =
      !isPortalDomain || !theme || theme.featuresEnabled.userRegistration !== false
    const passwordResetEnabled =
      !isPortalDomain || !theme || theme.featuresEnabled.passwordReset !== false

    return (
      <Routes>
        <Route
          path="/register"
          element={registrationEnabled ? <RegisterPage /> : <Navigate to="/login" replace />}
        />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/forgot-password"
          element={passwordResetEnabled ? <ForgotPasswordPage /> : <Navigate to="/login" replace />}
        />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/invite/:token" element={<InviteAcceptPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  // Mais de uma org compatível e a aba não escolheu: pergunta antes de qualquer tela (#1145)
  if (orgChoiceRequired) {
    return <OrgChoicePage />
  }

  // Verificar tipo de organização (pode estar em organization.type ou organization_type)
  const organizationType = user?.organization?.type
  const hasAccess = organizationType === 'end_customer'

  if (!hasAccess) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          <h1 className="mb-2 text-2xl font-bold text-purple-600">Acesso Negado</h1>
          <p className="mb-4 text-gray-600">Esta aplicação é restrita a Portal do Cliente</p>
          <p className="mb-4 text-sm text-gray-500">
            Tipo da organização: {organizationType || 'Não definido'}
          </p>
          <button
            onClick={() => useAuthStore.getState().logout()}
            className="rounded-lg bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
          >
            Fazer Logout
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
      <Routes>
        {/* Invite accept page - outside MainLayout */}
        <Route path="/invite/:token" element={<InviteAcceptPage />} />
        {/* Popup console route - outside MainLayout to avoid navbar/sidebar */}
        <Route path="/instances/:vmId/console/popup" element={<VMConsolePopupPage />} />
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/register" element={<Navigate to="/dashboard" replace />} />
        <Route path="/forgot-password" element={<Navigate to="/dashboard" replace />} />
        <Route path="/reset-password" element={<Navigate to="/dashboard" replace />} />
        <Route path="/verify-email" element={<Navigate to="/dashboard" replace />} />

        <Route path="/" element={<MainLayout />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route
            path="dashboard"
            element={
              <PortalFeatureRoute feature="dashboard">
                <DashboardPage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="marketplace"
            element={
              <PortalFeatureRoute feature="marketplace">
                <MarketplacePage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="instances"
            element={
              <PortalFeatureRoute feature="instances">
                <InstancesPage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="instances/:vmId"
            element={
              <PortalFeatureRoute feature="instances">
                <InstanceDetailPage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="monitoring"
            element={
              <PortalFeatureRoute feature="monitoring">
                <MonitoringPage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="backups"
            element={
              <PortalFeatureRoute feature="backups">
                <BackupsPage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="networking"
            element={
              <PortalFeatureRoute feature="networking">
                <NetworkingPage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="gateway"
            element={
              <PortalFeatureRoute feature="gateway">
                <GatewayPage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="storage"
            element={
              <PortalFeatureRoute feature="storage">
                <StoragePage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="analytics"
            element={
              <PortalFeatureRoute feature="analytics">
                <AnalyticsPage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="team"
            element={
              <PortalFeatureRoute feature="team">
                <TeamPage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="billing"
            element={
              <PortalFeatureRoute feature="billing">
                <BillingPage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="resellers"
            element={
              <FreeCustomerOnlyRoute>
                <ResellersPage />
              </FreeCustomerOnlyRoute>
            }
          />
          <Route
            path="my-associations"
            element={
              <FreeCustomerOnlyRoute>
                <MyAssociationsPage />
              </FreeCustomerOnlyRoute>
            }
          />
          <Route path="account" element={<AccountPage />} />
          <Route
            path="support"
            element={
              <PortalFeatureRoute feature="support">
                <SupportPage />
              </PortalFeatureRoute>
            }
          />
          <Route
            path="notifications"
            element={
              <PortalFeatureRoute feature="notifications">
                <NotificationsPage />
              </PortalFeatureRoute>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </>
  )
}

export default App
